/*
 * This file contains the validation functions used to validate the user inputs

  The functions are:
  - checkEmptyInput(input)
  - checkPasswordMatch(password, passwordConfirm)
  - checkPasswordStrength(password)
  - checkInputIsValidNationalIDOrPassport(input)
  - checkBirthDateValidity(birthDate)
  - checkEmailValidity(email)
  - checkPhoneValidity(phone)
  - checkArabicNameValidity(name)
  - checkEnglishNameValidity(name)
  - checkInputLength(input, minLength, maxLength)
  - checkInputIsNumber(input)
*/

/*
  Check if the input is empty
  @param {string} input - The input to be checked
  @returns {boolean} - The result of the check
*/
const checkEmptyInput = (input) => {
  return input.trim() === '';
};

/*
  Check if the password and passwordConfirm match
  @param {string} password - The password to be checked
  @param {string} passwordConfirm - The passwordConfirm to be checked
  @returns {boolean} - The result of the check
*/
const checkPasswordMatch = (password, passwordConfirm) => {
  return password === passwordConfirm;
};

/*
  Check if the password is strong enough
  @param {string} password - The password to be checked
  @returns {boolean} - The result of the check
*/
const checkPasswordStrength = (password) => {
  // Password must contain at least 8 characters
  // Password must contain at least 1 uppercase letter
  // Password must contain at least 1 lowercase letter
  // Password must contain at least 1 number
  // Password must contain at least 1 special character from @, #, $, %, ^, &, *, (, )
  const passwordRegex =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&*()]).{8,}$/;
  return checkEmptyInput(password) || passwordRegex.test(password);
};

/*
  Check if the input is a valid national ID or passport number
  @param {string} input - The input to be checked
  @returns {boolean} - The result of the check
*/
const checkInputIsValidNationalIDOrPassport = (input) => {
  // Check if the input is a valid national ID or passport number
  // National ID must be 14 digits
  // Passport number must be 9 digits
  const nationalIDRegex = /^\d{14}$/;
  const passportRegex = /^\d{9}$/;
  return (
    checkEmptyInput(input) ||
    nationalIDRegex.test(input) ||
    passportRegex.test(input)
  );
};

/*
  Check if the birthDate is valid (accepts any date format yyyy-mm-dd)
  @param {string} birthDate - The birthDate to be checked
  @returns {boolean} - The result of the check
*/
const checkBirthDateValidity = (birthDate) => {
  // Check if the birthDate is valid (accepts any date format yyyy-mm-dd)
  const birthDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return checkEmptyInput(birthDate) || birthDateRegex.test(birthDate);
};

/*
  Check if the email is valid
  @param {string} email - The email to be checked
  @returns {boolean} - The result of the check
*/
const checkEmailValidity = (email) => {
  // Updated regex to disallow consecutive dots
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
  return checkEmptyInput(email) || emailRegex.test(email);
};

/*
  Check if the phone number is valid (accepts any phone number format)
  @param {string} phone - The phone number to be checked
  @returns {boolean} - The result of the check
*/
const checkPhoneValidity = (phone) => {
  // Check if the phone number is valid (accepts any phone number format)
  const phoneRegex = /^(\+)?(\d{1,3})?(\d{10,12})$/;
  return checkEmptyInput(phone) || phoneRegex.test(phone);
};

/*
  Check if the name is valid (accepts Arabic characters only) and contain at least 4 words separated by spaces with no leading or trailing spaces
  @param {string} name - The name to be checked
  @returns {boolean} - The result of the check
*/
const checkArabicNameValidity = (name) => {
  // Check if the name is valid (accepts Arabic characters only) and contain at least 4 words separated by spaces with no leading or trailing spaces
  const nameRegex = /^[\u0621-\u064A\s]{4,}$/;
  return checkEmptyInput(name) || nameRegex.test(name);
};

/*
  Check if the name is valid (accepts English characters only) and contain at least 4 words separated by spaces with no leading or trailing spaces
  @param {string} name - The name to be checked
  @returns {boolean} - The result of the check
*/
const checkEnglishNameValidity = (name) => {
  // Check if the name is valid (accepts English characters only) and contain at least 4 words separated by spaces with no leading or trailing spaces
  const nameRegex = /^[a-zA-Z]{3,}(\s[a-zA-Z]{3,}){3,}$/;
  return checkEmptyInput(name) || nameRegex.test(name);
};

/* 
  Check if the input length is within the specified range
  @param {string} input - The input to be checked
  @param {number} minLength - The minimum length of the input
  @param {number} maxLength - The maximum length of the input
  @returns {boolean} - The result of the check
*/
const checkInputLength = (input, minLength, maxLength) => {
  return input.length >= minLength && input.length <= maxLength;
};

/*
  Check if the input is a number
  @param {string} input - The input to be checked
  @returns {boolean} - The result of the check
*/
const checkInputIsNumber = (input) => {
  return !isNaN(input);
};

/*
  Check if the username is valid (accepts English characters and numbers and . and _ only) and contain at least 4 characters with no leading or trailing spaces
  @param {string} username - The username to be checked
  @returns {boolean} - The result of the check
*/
const checkUsernameValidity = (username) => {
  // Check if the username is valid (accepts English characters and numbers and . and _ only) and contain at least 4 characters with no leading or trailing spaces
  const usernameRegex = /^[a-zA-Z0-9._]{4,}$/;
  return checkEmptyInput(username) || usernameRegex.test(username);
};

const validator = {
  checkPasswordMatch,
  checkPasswordStrength,
  checkInputIsValidNationalIDOrPassport,
  checkBirthDateValidity,
  checkEmailValidity,
  checkPhoneValidity,
  checkArabicNameValidity,
  checkEnglishNameValidity,
  checkInputLength,
  checkInputIsNumber,
  checkUsernameValidity,
};

export default validator;
